import { CommonModule } from '@angular/common';
import { Component, Input, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';

export interface Column {
  label: string;
  render?: (row: any, index: number) => string | number | null;
  key?: string;
  className?: string;
  /** Activer la troncature avec "Voir plus" pour cette colonne */
  truncate?: boolean;
  /** Nombre de caractères avant troncature (défaut: 80) */
  truncateLength?: number;
}

@Component({
  selector: 'app-ui-table',
  imports: [CommonModule],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  standalone: true
})
export class UiTable implements OnChanges {
  @Input() columns: Column[] = [];
  @Input() data: any[] = [];
  @Input() emptyMessage: string = 'Aucune donnée trouvée';
  @Input() actions: TemplateRef<any> | null = null;

  /** Nombre de lignes par page */
  @Input() pageSize: number = 10;
  /** Options disponibles pour le sélecteur de taille de page */
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];

  // ── État pagination ──────────────────────────
  currentPage = 1;
  totalPages = 1;
  pagedData: any[] = [];

  // ── État des lignes expandées ────────────────
  /** Map rowIndex -> Set<colIndex> des cellules dépliées */
  expandedCells = new Map<number, Set<number>>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['pageSize']) {
      this.currentPage = 1;
      this.updatePage();
    }
  }

  // ── Pagination ───────────────────────────────

  updatePage(): void {
    const total = this.data?.length ?? 0;
    this.totalPages = Math.max(1, Math.ceil(total / this.pageSize));
    this.currentPage = Math.min(this.currentPage, this.totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedData = (this.data ?? []).slice(start, start + this.pageSize);

    // Réinitialise les expansions lors du changement de page
    this.expandedCells.clear();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
    this.updatePage();
  }

  get pageNumbers(): number[] {
    const range: number[] = [];
    const delta = 2;
    const left = Math.max(1, this.currentPage - delta);
    const right = Math.min(this.totalPages, this.currentPage + delta);

    if (left > 1) range.push(1);
    if (left > 2) range.push(-1); // ellipsis
    for (let i = left; i <= right; i++) range.push(i);
    if (right < this.totalPages - 1) range.push(-1); // ellipsis
    if (right < this.totalPages) range.push(this.totalPages);

    return range;
  }

  get startIndex(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.data?.length ?? 0);
  }

  // ── Troncature ───────────────────────────────

  isTruncated(col: Column, value: string): boolean {
    if (!col.truncate) return false;
    const limit = col.truncateLength ?? 80;
    return typeof value === 'string' && value.length > limit;
  }

  getCellValue(row: any, col: Column): string {
    return String(row[col.key ?? ''] ?? '');
  }

  getTruncatedValue(col: Column, value: string): string {
    const limit = col.truncateLength ?? 80;
    return value.slice(0, limit) + '…';
  }

  isCellExpanded(rowIndex: number, colIndex: number): boolean {
    return this.expandedCells.get(rowIndex)?.has(colIndex) ?? false;
  }

  toggleCell(rowIndex: number, colIndex: number): void {
    if (!this.expandedCells.has(rowIndex)) {
      this.expandedCells.set(rowIndex, new Set());
    }
    const set = this.expandedCells.get(rowIndex)!;
    if (set.has(colIndex)) {
      set.delete(colIndex);
    } else {
      set.add(colIndex);
    }
    // Déclencher la détection de changement manuellement
    this.expandedCells = new Map(this.expandedCells);
  }
}